"use client";

import * as React from "react";
import { ThemeProvider } from "next-themes";
import { TelemetryProvider } from "@workspace/telemetry/provider";
import {
  AppHeader,
  type HeaderVariant,
} from "@workspace/ui/components/app-header";
import { HeaderNav } from "@workspace/ui/components/floating-header";
import type { MenuItem } from "@workspace/ui/components/floating-header";
import { BrandThemeProvider } from "@workspace/ui/hooks/useBrandTheme";
import type { ModeRequest } from "@workspace/ui/brand-themes";
import { AppFrame, AppFrameProvider } from "@workspace/ui/components/app-frame";

type RootLayoutProps = {
  children: React.ReactNode;
  /** Sub-app identifier shown after the brand (e.g. "benchmarks", "kb") */
  appName?: string;
  /** Header visual variant */
  headerVariant?: HeaderVariant;
  /** Content to render in the header's center slot */
  headerContent?: React.ReactNode;
  /** Menu items for the center slot (backward compat, used if headerContent not provided) */
  links?: MenuItem[];
  footer?: React.ReactNode;
  className?: string;
  /**
   * Initial brand-theme slug, typically read from `gz_theme` cookie SSR-side
   * via `readBrandThemeFromCookies()` and passed down to avoid a hydration
   * flash. Pass `null` to explicitly unthemed; omit to fall back to client
   * cookie read at mount.
   */
  initialBrandThemeSlug?: string | null;
  /**
   * Initial brand-theme mode (auto/light/dark). Same SSR cookie source as
   * the slug. Defaults to "auto" client-side if omitted.
   */
  initialBrandThemeMode?: ModeRequest;
  /**
   * A left navigation bar, laid out by `AppFrame`: docked on wide screens, a
   * drawer below them, opened from the header's own toggle. For an app whose
   * sidebar needs no providers of its own. An app that does (messaging, whose sidebar
   * needs auth and workspace context) leaves this out and renders `<AppFrame>`
   * itself inside those providers — the state comes from here either way.
   */
  sidebar?: React.ReactNode;
  /**
   * Names the sidebar: its drawer's accessible name and the mobile bar's
   * title. Defaults to `appName`. A `sidebar` given here is declared an
   * essential list (adr-0023) — an app whose sidebar is something else
   * renders `<AppFrame>` itself.
   */
  sidebarTitle?: string;
};

export function RootLayout({
  children,
  appName,
  headerVariant = "floating",
  headerContent,
  links = [],
  footer,
  className,
  initialBrandThemeSlug,
  initialBrandThemeMode,
  sidebar,
  sidebarTitle,
}: RootLayoutProps) {
  return (
    <TelemetryProvider appName={appName ?? "unknown"}>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
        enableColorScheme
      >
        <BrandThemeProvider initialSlug={initialBrandThemeSlug} initialMode={initialBrandThemeMode}>
          {/* Every app gets the frame's state, so any pane can use
              `useAppFrame()` / `AppFrameToggle` / `AppFrameRightSlot`. The
              layout itself renders only where an app asks for it.

              It wraps the HEADER too, not just the main pane: the header holds
              the left side's toggle (`AppFrameHeaderToggle`), which is the one
              affordance that must not depend on the route — a collapsed docked
              sidebar renders nothing, so without it there is no way back. */}
          <AppFrameProvider storageKey={`app-frame:${appName ?? "app"}`}>
            <AppHeader variant={headerVariant} appName={appName}>
              {headerContent ??
                (links.length > 0 ? <HeaderNav links={links} /> : null)}
            </AppHeader>
            <main className={className}>
              {sidebar !== undefined ? (
                <AppFrame
                  left={{
                    role: "essential",
                    content: "list",
                    title: sidebarTitle ?? appName ?? "Navigation",
                    node: sidebar,
                  }}
                  mobileTitle={sidebarTitle ?? appName}
                >
                  {children}
                </AppFrame>
              ) : (
                children
              )}
            </main>
          </AppFrameProvider>
          {footer}
        </BrandThemeProvider>
      </ThemeProvider>
    </TelemetryProvider>
  );
}
